import { css } from '@styled-system/css';
import { circle, stack } from '@styled-system/patterns';
import { token } from '@styled-system/tokens';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { MoonLoader } from 'react-spinners';

import { AnalysisItemData } from '@/features/analysisMenu/types';
import { InteractionOverlay } from '@/shared/components/ui/InteractionOverlay';
import { NewStatusIndicator } from '@/shared/components/ui/NewStatusIndicator';
import { OptimizedBackgroundImage } from '@/shared/components/ui/OptimizedBackgroundImage';
import { Separator } from '@/shared/components/ui/Separator';

interface AnalysisMenuItemProps {
  analysis: AnalysisItemData;
  isSelected?: boolean;
  isBlurred?: boolean;
  enableMenu?: boolean;
  onClick?: () => void;
  onRename?: (newName: string) => void;
  onStop?: () => void;
  onDelete?: () => void;
}

export const AnalysisMenuItem: FC<AnalysisMenuItemProps> = ({
  analysis,
  isSelected = false,
  isBlurred = false,
  enableMenu = true,
  onClick,
  onRename,
  onStop,
  onDelete,
}) => {
  const isInProgress = analysis.status === 'starting' || analysis.status === 'active';

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(analysis.name);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Handle save
  const handleSave = () => {
    const trimmedValue = editValue.trim();
    if (trimmedValue && trimmedValue !== analysis.name) {
      onRename?.(trimmedValue);
    }
    setIsEditing(false);
    setEditValue(analysis.name);
  };

  // Handle cancel
  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(analysis.name);
  };

  // Handle key press
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  // Handle blur
  const handleBlur = () => {
    handleSave();
  };

  // Start editing
  const startEditing = () => {
    setIsEditing(true);
    setEditValue(analysis.name);
  };

  const containerStyles = stack({
    direction: 'column',
    w: '100%',
    gap: 0,
  });

  const cardStyles = stack({
    direction: 'row',
    h: '3.25rem',
    alignItems: 'center',
    justifyContent: 'space-between',
    w: '100%',
    cursor: 'pointer',
    borderRadius: isSelected ? 'sm' : 0,
    pr: '0.81rem',
    pl: '0.15rem',
    bg: isSelected ? 'bg.selected' : 'transparent',

    _hover: {
      cursor: 'pointer',
      bg: 'bg.selected',
      borderRadius: 'sm',
    },
  });

  const contentStyles = stack({
    direction: 'row',
    alignItems: 'center',
    gap: '0.4rem',
    px: '0.1875rem',
  });

  const analysisProductImageStyles = circle({
    w: '1.875rem',
    h: '1.875rem',
    overflow: 'hidden',
    flexShrink: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    bg: 'bg.selected',
  });

  const infoStyles = stack({
    direction: 'column',
    alignItems: 'flex-start',
    gap: '0.1875rem',
  });

  const nameContainerStyles = stack({
    direction: 'row',
    alignItems: 'center',
    gap: '0.4rem',
  });

  const nameStyles = css({
    fontSize: 'sm',
    fontWeight: 500,
    color: 'font.secondary',
    lineHeight: 1.07,
    textAlign: 'left',
    pb: '0.125rem',
    outline: '1px solid transparent',
    h: '1rem',
  });

  const inputStyles = css({
    fontSize: 'sm',
    fontWeight: 500,
    color: 'font.secondary',
    lineHeight: 1.07,
    textAlign: 'left',
    pb: '0.125rem',
    bg: 'transparent',
    border: 'none',
    outline: '1px solid transparent',
    h: '1rem',
    p: 0,
    m: 0,
    width: '100%',
    fontFamily: 'inherit',

    _focus: {
      outline: '1px solid',
      outlineColor: 'accent.primary',
      borderRadius: 'xs',
      px: 'xs',
    },
  });

  const statusIndicatorStyles = css({
    w: '0.4375rem',
    h: '0.4375rem',
    mb: '0.1rem',
  });

  const statusContainerStyles = stack({
    direction: 'row',
    alignItems: 'center',
    gap: '0.0625rem', // 1px
  });

  const statusIconStyles = css({
    w: '0.625rem',
    h: '0.625rem',
    flexShrink: 0,
  });

  const statusTextColor =
    analysis.status === 'starting' || analysis.status === 'active'
      ? 'accent.primary'
      : analysis.status === 'inactive'
        ? 'font.lowkey'
        : 'accent.red';
  const statusTextStyles = css({
    fontSize: '2xs',
    fontWeight: 500,
    lineHeight: 1.07,
    whiteSpace: 'nowrap',
    textAlign: 'center',
    color: statusTextColor,
  });

  const separatorStyles = css({
    alignSelf: 'center',
    w: 'calc(100% - 1rem)',
    mt: '-1px',
  });

  const getStatusText = (): string | null => {
    switch (analysis.status) {
      case 'starting':
        return 'Analyse en cours de démarrage...';
      case 'active':
        return (analysis.newSells ?? 0) > 0 ? `${analysis.newSells} nouvelles ventes !` : null;
      case 'inactive':
        return (analysis.analysedProductCount ?? 0) > 0 ? `${analysis.analysedProductCount} produits analysés !` : null;
      case 'error':
        return 'Erreur de traitement';
      case 'removed':
        return 'Analyse supprimée';
      default:
        return null;
    }
  };

  const StarLogo = () => (
    <svg
      width="100%"
      preserveAspectRatio="xMidYMid meet"
      viewBox="0 0 10 10"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_11_234)">
        <path
          d="M5.00024 1.25L5.59616 3.46325C5.66887 3.73333 5.70524 3.86837 5.77699 3.97877C5.84049 4.07645 5.92379 4.15973 6.02149 4.22325C6.13187 4.295 6.26691 4.33138 6.53699 4.40408L8.75024 5L6.53699 5.59592C6.26691 5.66862 6.13187 5.705 6.02149 5.77675C5.92379 5.84025 5.84049 5.92354 5.77699 6.02125C5.70524 6.13163 5.66887 6.26667 5.59616 6.53675L5.00024 8.75L4.40433 6.53675C4.33162 6.26667 4.29524 6.13163 4.22349 6.02125C4.15998 5.92354 4.0767 5.84025 3.97902 5.77675C3.86862 5.705 3.73358 5.66862 3.4635 5.59592L1.25024 5L3.4635 4.40408C3.73358 4.33138 3.86862 4.295 3.97902 4.22325C4.0767 4.15973 4.15998 4.07645 4.22349 3.97877C4.29524 3.86837 4.33162 3.73333 4.40433 3.46325L5.00024 1.25Z"
          fill="#5E69D3"
          stroke="#8893FF"
          strokeWidth="0.833333"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_11_234">
          <rect width="10" height="10" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );

  const statusText = getStatusText();

  return (
    <div className={containerStyles}>
      <div
        className={cardStyles}
        onClick={onClick}
        style={isBlurred ? { filter: 'blur(2px)', opacity: 0.6 } : undefined}
      >
        <div className={contentStyles}>
          {/* Analysis Product Image */}
          {analysis.status === 'starting' ? (
            <MoonLoader size={20} color={token.var('colors.accent.primary')} />
          ) : analysis.imageUrl ? (
            <OptimizedBackgroundImage className={analysisProductImageStyles} src={analysis.imageUrl} />
          ) : (
            <div className={analysisProductImageStyles} />
          )}

          {/* Info */}
          <div className={infoStyles}>
            {/* Name avec indicateur */}
            <div className={nameContainerStyles}>
              {isEditing ? (
                <input
                  ref={inputRef}
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                  className={inputStyles}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <div className={nameStyles}>{analysis.name}</div>
              )}
              {analysis.status === 'active' && (analysis.newSells ?? 0) > 0 && !isEditing && (
                <NewStatusIndicator className={statusIndicatorStyles} />
              )}
            </div>

            {/* Status */}
            {statusText && (
              <div className={statusContainerStyles}>
                {analysis.status === 'active' && (analysis.newSells ?? 0) > 0 && (
                  <div className={statusIconStyles}>
                    <StarLogo />
                  </div>
                )}
                <div className={statusTextStyles}>{statusText}</div>
              </div>
            )}
          </div>
        </div>
        {enableMenu && (
          <InteractionOverlay
            items={[
              {
                icon: 'rename',
                label: 'Renommer',
                onClick: () => {
                  startEditing();
                },
              },
              isInProgress
                ? {
                    danger: true,
                    icon: 'stop',
                    label: 'Arrêter',
                    onClick: () => onStop?.(),
                  }
                : {
                    danger: true,
                    icon: 'trash',
                    label: 'Supprimer',
                    onClick: () => onDelete?.(),
                  },
            ]}
          />
        )}
      </div>

      <Separator orientation="horizontal" className={separatorStyles} />
    </div>
  );
};
